import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CartContext } from "../context/cartcontext";
import { formatPrice } from "../data/products";
import ContinueShoppingButton from "../components/ContinueShoppingButton";
import ProductImage from "../components/ProductImage";
import PageHero from "../components/PageHero";
import { getActiveProducts, getProductBySlug } from "../services/storefrontProducts";
import { getDisplayPrice, getRelatedProducts } from "../utils/productDetailUtils";
import { PROBIODELIGHTS_CUSTOMER_LAUNCH_ENABLED } from "../config/featureFlags";
import SEO, { SITE_NAME, SITE_URL } from "../components/SEO";

const ProductDetail = () => {
  const { slug } = useParams();
  const { addToCart } = useContext(CartContext);
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadProduct = async () => {
      setLoading(true);
      setError("");

      try {
        const [item, catalog] = await Promise.all([getProductBySlug(slug), getActiveProducts()]);

        if (!cancelled) {
          setProduct(item);
          setRelatedProducts(getRelatedProducts(catalog || [], item || {}));
          setSelectedOption(item?.options?.[0] ?? null);
          setQuantity(1);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Unable to load product.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProduct();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const displayPrice = useMemo(() => getDisplayPrice(product, selectedOption), [product, selectedOption]);

  const productStructuredData = useMemo(() => {
    if (!product) return null;

    const image = product.image?.startsWith("http")
      ? product.image
      : `${SITE_URL}${product.image?.startsWith("/") ? product.image : `/${product.image || "zeamays-favicon.png"}`}`;

    return {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      description: product.description,
      image,
      brand: { "@type": "Brand", name: SITE_NAME },
      offers: {
        "@type": "Offer",
        url: `${SITE_URL}/product/${product.slug}`,
        priceCurrency: "NGN",
        price: displayPrice,
        availability: product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      },
    };
  }, [displayPrice, product]);

  if (loading) {
    return <div className="px-6 py-32 text-center text-slate-700">Loading product...</div>;
  }

  if (error) {
    return <div className="px-6 py-32 text-center text-red-600">{error}</div>;
  }

  if (!product) {
    return <div className="px-6 py-32 text-center text-red-600">Product not found.</div>;
  }

  const handleAddToCart = () => {
    if (!selectedOption) {
      return;
    }

    addToCart({
      id: `${product.id}:${selectedOption.id}:${quantity}`,
      productId: product.id,
      productSlug: product.slug,
      name: product.name,
      selectedSize: selectedOption.label,
      unitPrice: displayPrice,
      optionLabel: selectedOption.label,
      price: displayPrice,
      image: product.image,
      quantity,
    });
  };

  return (
    <div className="min-h-screen bg-[#f7fbef]">
      <SEO
        title={`${product.name} | Nigerian Food Products | ${SITE_NAME}`}
        description={`${product.description || `Order ${product.name} from Zea Mays Foods.`} Delivery is available across Nigeria; delivery fees are confirmed after your order.`.slice(0, 160)}
        path={`/product/${product.slug}`}
        image={product.image}
        structuredData={productStructuredData}
      />
      <PageHero
        eyebrow={[PROBIODELIGHTS_CUSTOMER_LAUNCH_ENABLED ? (product.business_line || "Pantry") : null, product.category].filter(Boolean).join(" / ")}
        title={product.name}
        description={product.description || "Explore the product details, select a size, and add it to your cart when you're ready."}
        image={product.image}
        imageAlt={product.name}
        primaryAction={{ to: "/cart", label: "Go to Cart" }}
        secondaryAction={{ to: "/products", label: "Browse Products" }}
        showImageCaption={false}
        compact
      />

      <div className="mx-auto max-w-7xl px-6 py-12">
        <div className="mb-8 flex justify-start">
          <ContinueShoppingButton />
        </div>

        <div className="grid gap-10 rounded-[2rem] border border-lime-200 bg-white p-6 shadow-sm lg:grid-cols-[1.05fr_0.95fr] lg:p-10">
          <div className="flex min-h-[320px] items-center justify-center overflow-hidden rounded-[1.5rem] bg-lime-50 p-4 sm:min-h-[420px]">
            <ProductImage
              src={product.image}
              alt={product.name}
              className="h-full w-full max-w-full scale-[1.45] transform-gpu"
              fit="cover"
            />
          </div>

          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-red-700">
              {product.category || "Product"}
            </p>
            <h1 className="mt-3 text-4xl font-bold text-slate-900">{product.name}</h1>
            <p className="mt-4 text-lg leading-8 text-slate-600">{product.description}</p>

            <div className="mt-6 rounded-2xl border border-lime-200 bg-lime-50 px-4 py-4">
              <p className="font-semibold text-green-900">Nationwide delivery across Nigeria</p>
              <p className="mt-1 text-sm leading-6 text-slate-700">
                After your order is confirmed, our team will contact you to arrange the best transport or dispatch option for your location and confirm the delivery fee. Delivery fees are separate from the product total.
              </p>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <span
                className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${
                  product.stock > 0 ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                }`}
              >
                {product.stock > 0 ? "In stock" : "Out of stock"}
              </span>
              <span className="text-sm text-slate-500">
                {product.stock > 0 ? `${product.stock} available` : "Currently unavailable"}
              </span>
            </div>

            <div className="mt-8 rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-medium text-slate-500">Price</p>
              <p className="mt-2 text-3xl font-bold text-red-700">{formatPrice(displayPrice)}</p>
            </div>

            {product.options?.length ? (
              <div className="mt-8">
                <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-slate-600">
                  Choose size
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {product.options.map((option) => {
                    const isSelected = selectedOption?.id === option.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => setSelectedOption(option)}
                        className={`rounded-2xl border p-4 text-left transition ${
                          isSelected ? "border-red-700 bg-red-50" : "border-slate-200 bg-white hover:border-red-300"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-semibold text-slate-900">{option.label}</span>
                          <span className="font-bold text-red-700">{formatPrice(option.price)}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <div className="flex items-center rounded-2xl border border-slate-200 bg-white px-3 py-2">
                <button
                  type="button"
                  onClick={() => setQuantity((value) => Math.max(1, value - 1))}
                  className="px-2 text-xl font-semibold text-slate-700"
                >
                  -
                </button>
                <span className="min-w-10 text-center text-lg font-semibold text-slate-900">{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity((value) => value + 1)}
                  className="px-2 text-xl font-semibold text-slate-700"
                >
                  +
                </button>
              </div>
              <button
                type="button"
                onClick={handleAddToCart}
                disabled={!selectedOption || product.stock <= 0}
                className="rounded-2xl bg-red-700 px-6 py-3 font-semibold text-white transition hover:bg-red-800 disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                {product.stock > 0 ? "Add to Cart" : "Out of Stock"}
              </button>
            </div>
          </div>
        </div>

        {relatedProducts.length ? (
          <div className="mt-12">
            <div className="mb-6 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.28em] text-red-700">Related products</p>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900">
                  More in {product.category || "this category"}
                </h2>
              </div>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {relatedProducts.map((relatedProduct) => (
                <Link
                  key={relatedProduct.slug}
                  to={`/product/${relatedProduct.slug}`}
                  className="flex h-full flex-col overflow-hidden rounded-[1.5rem] border border-lime-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-lime-50">
                    <ProductImage
                      src={relatedProduct.image}
                      alt={relatedProduct.name}
                      className="h-full w-full"
                      width="400"
                      height="300"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-4">
                    <h3 className="text-lg font-semibold text-slate-900">{relatedProduct.name}</h3>
                    <p className="mt-2 text-sm text-slate-600">{relatedProduct.category}</p>
                    <p className="mt-4 font-semibold text-red-700">
                      {formatPrice(relatedProduct.options?.[0]?.price ?? relatedProduct.price)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ProductDetail;
